import { useCallback } from 'react';

import { mergeChildren } from './data-extractors.ts';
import PageTreeChildrenQuery from './queries/__generated__/PageTreeChildrenQuery.graphql.ts';
import { CHILDREN_PAGE_SIZE } from './paginationLimits.ts';
import { usePageTreeState } from './usePageTreeState.ts';
import type { QueryChildren } from './usePageTreeState.ts';
import { useImperativeQuery } from './useImperativeQuery.ts';
import type { GraphQLPageStatus } from './pageTreeStatuses.ts';

type UsePageTreeChildren = ({
	statuses,
	isSuperAdmin,
}: {
	statuses?: GraphQLPageStatus[];
	isSuperAdmin?: boolean;
}) => {
	queryChildren: QueryChildren;
	reloadChildren: () => void;
};

export const usePageTreeChildren: UsePageTreeChildren = ({ statuses, isSuperAdmin }) => {
	const [state, actions] = usePageTreeState();

	const getPageChildren = useImperativeQuery<any, any>(PageTreeChildrenQuery);

	const queryChildren: QueryChildren = useCallback(
		(pageId, after, allChildren = { count: 0, nodes: [] }, iteration = 0) => {
			return getPageChildren({
				contentId: pageId,
				statuses,
				first: CHILDREN_PAGE_SIZE,
				after,
				isSuperAdmin: isSuperAdmin || false,
			})
				.then((response) => {
					if (response.data) {
						const children = response.data?.ptpage?.children;
						const mergedChildren = children ? mergeChildren(allChildren, children) : { nodes: [] };
						return children?.pageInfo?.hasNextPage &&
							after !== children?.pageInfo?.endCursor && // ensure the new cursor is different
							iteration < 100 // prevent a potential infinite loop
							? queryChildren(pageId, children?.pageInfo?.endCursor, mergedChildren, iteration + 1)
							: { ...mergedChildren, error: null };
					}
					return { nodes: [], error: null };
				})
				.catch((error) => {
					actions.handleLoadingError(error, 'children');
					return { nodes: [], error };
				});
		},
		[getPageChildren, isSuperAdmin, statuses, actions],
	);

	const reloadChildren = useCallback(() => {
		// @ts-ignore
		void queryChildren(state.focusedPageId);
		// @ts-ignore
	}, [queryChildren, state.focusedPageId]);

	return { queryChildren, reloadChildren };
};
